import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/lib/auth-context";
import { Spinner } from "@/components/ui/spinner";
import { ShieldCheck, ArrowLeft, KeyRound, AlertTriangle } from "lucide-react";

interface TwoFactorChallengeProps {
  challengeToken: string;
  email: string;
  firstName: string | null;
  onSuccess: () => void;
  onBack: () => void;
}

export function TwoFactorChallenge({
  challengeToken,
  email,
  firstName,
  onSuccess,
  onBack,
}: TwoFactorChallengeProps) {
  const { complete2FA } = useAuth();
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [trustDevice, setTrustDevice] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBackupCode, setShowBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [backupCodesRemaining, setBackupCodesRemaining] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only take the last digit
    setCode(newCode);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (value && index === 5) {
      const fullCode = newCode.join("");
      if (fullCode.length === 6) {
        handleSubmit(fullCode);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < 6; i++) {
      newCode[i] = pastedData[i] || "";
    }
    setCode(newCode);
    setError(null);

    // Auto-submit if 6 digits pasted
    if (pastedData.length === 6) {
      handleSubmit(pastedData);
    }
  };

  const handleSubmit = async (codeToSubmit?: string) => {
    const finalCode = codeToSubmit || code.join("");
    if (finalCode.length !== 6 && !showBackupCode) {
      setError("Ange alla 6 siffror");
      return;
    }

    setIsLoading(true);
    setError(null);

    const result = await complete2FA(
      challengeToken,
      showBackupCode ? backupCode : finalCode,
      trustDevice
    );

    if (result.success) {
      if (result.backupCodesRemaining !== undefined) {
        setBackupCodesRemaining(result.backupCodesRemaining);
      }
      onSuccess();
    } else {
      setError(result.error || "Ogiltig kod. Försök igen.");
      // Clear code on error
      if (!showBackupCode) {
        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    }

    setIsLoading(false);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit();
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Tvåfaktorsverifiering
        </h2>
        <p className="text-gray-600">
          {firstName ? `Hej ${firstName}! ` : ""}
          Ange koden från din autentiseringsapp.
        </p>
        <p className="text-sm text-gray-500 mt-1">{email}</p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Low backup codes warning */}
      {backupCodesRemaining !== null && backupCodesRemaining <= 3 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-100 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-700">
            <p className="font-medium">Få backup-koder kvar</p>
            <p>Du har bara {backupCodesRemaining} backup-koder kvar. Generera nya i inställningarna.</p>
          </div>
        </div>
      )}

      <form onSubmit={handleFormSubmit}>
        {!showBackupCode ? (
          <>
            {/* TOTP Code Input */}
            <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isLoading}
                  className="w-12 h-14 text-center text-2xl font-semibold border border-gray-300 rounded-lg
                    focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none
                    disabled:bg-gray-100 disabled:cursor-not-allowed
                    transition-all duration-150"
                />
              ))}
            </div>

            {/* Trust device checkbox */}
            <label className="flex items-center gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                disabled={isLoading}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Kom ihåg denna enhet i 30 dagar
              </span>
            </label>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading || code.some((d) => !d)}
              className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg
                hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 outline-none
                disabled:bg-blue-300 disabled:cursor-not-allowed
                transition-colors duration-150 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" />
                  <span>Verifierar...</span>
                </>
              ) : (
                "Verifiera"
              )}
            </button>

            {/* Use backup code link */}
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowBackupCode(true)}
                className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
              >
                <KeyRound className="w-4 h-4" />
                Använd backup-kod istället
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Backup Code Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Backup-kod
              </label>
              <input
                type="text"
                value={backupCode}
                onChange={(e) => {
                  setBackupCode(e.target.value.toUpperCase());
                  setError(null);
                }}
                placeholder="XXXXXXXX"
                disabled={isLoading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-lg font-mono
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none
                  disabled:bg-gray-100 disabled:cursor-not-allowed uppercase"
              />
              <p className="mt-2 text-sm text-gray-500">
                Backup-koder är 8 tecken och kan bara användas en gång.
              </p>
            </div>

            {/* Trust device checkbox */}
            <label className="flex items-center gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                disabled={isLoading}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Kom ihåg denna enhet i 30 dagar
              </span>
            </label>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading || backupCode.length < 6}
              className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg
                hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 outline-none
                disabled:bg-blue-300 disabled:cursor-not-allowed
                transition-colors duration-150 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" />
                  <span>Verifierar...</span>
                </>
              ) : (
                "Verifiera med backup-kod"
              )}
            </button>

            {/* Back to TOTP link */}
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  setShowBackupCode(false);
                  setBackupCode("");
                  setError(null);
                }}
                className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
              >
                <ShieldCheck className="w-4 h-4" />
                Använd autentiseringsapp istället
              </button>
            </div>
          </>
        )}

        {/* Back button */}
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" />
            Tillbaka till inloggning
          </button>
        </div>
      </form>
    </div>
  );
}
